/**
 * Shared presentational chips for the decision/flag/failure columns.
 *
 * Shared by the round-level expediente browser (and any future list view) so
 * decisions, priorities, and reason codes are styled identically. All labels are
 * the Spanish maps in `api/labels`; unknown codes fall back to the raw code.
 */

import { Chip, Stack, Typography } from "@mui/material";

import {
  FAILURE_CODE_LABEL,
  FLAG_CODE_LABEL,
  labelOrCode,
} from "../api/labels";
import type {
  EffectiveDecision,
  FinalDecisionCompact,
  Priority,
} from "../api/client";

// Keyed by the effective vocabulary (Decision + the human-only AWAITING_RESUBMISSION,
// decision 12) so it labels an adjudicated disposition too.
export const DECISION_LABEL: Record<EffectiveDecision, string> = {
  APPROVED: "Aprobado",
  REJECTED: "Rechazado",
  HUMAN_REVIEW: "Revisión humana",
  AWAITING_RESUBMISSION: "En espera de reenvío",
};

export const PRIORITY_LABEL: Record<Priority, string> = {
  HIGH: "Alta",
  NORMAL: "Normal",
};

/** Em dash placeholder for "no value" table cells. */
function Dash() {
  return (
    <Typography component="span" variant="body2" color="text.secondary">
      —
    </Typography>
  );
}

export function DecisionChip({
  decision,
  adjudicated = false,
}: {
  decision: EffectiveDecision;
  /** When true, the decision is a human verdict — shown as "… (adjudicado)". */
  adjudicated?: boolean;
}) {
  const color =
    decision === "APPROVED"
      ? "success"
      : decision === "REJECTED"
        ? "error"
        : decision === "AWAITING_RESUBMISSION"
          ? "secondary"
          : "warning";
  const base = DECISION_LABEL[decision] ?? decision;
  return (
    <Chip
      label={adjudicated ? `${base} (adjudicado)` : base}
      color={color}
      size="small"
      // Filled (vs outlined) so a human-adjudicated row reads as authoritative.
      variant={adjudicated ? "filled" : "outlined"}
    />
  );
}

export function PriorityChip({ priority }: { priority: Priority | null }) {
  if (priority === "HIGH") {
    return (
      <Chip label="Alta" color="error" size="small" sx={{ fontWeight: 600 }} />
    );
  }
  if (priority === "NORMAL") {
    return (
      <Chip label="Normal" color="default" size="small" variant="outlined" />
    );
  }
  return <Dash />;
}

/**
 * The "why" column: rejection failures (error-toned) followed by gray-zone flags
 * (HIGH flags error-toned, the rest neutral). Empty for a clean APPROVED row.
 */
export function ReasonChips({
  failures,
  flags,
}: {
  failures: FinalDecisionCompact["failures"];
  flags: FinalDecisionCompact["flags"];
}) {
  if (!failures.length && !flags.length) return <Dash />;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {failures.map((f, i) => (
        <Chip
          key={`fail-${f.code}-${i}`}
          label={labelOrCode(FAILURE_CODE_LABEL, f.code)}
          size="small"
          color="error"
          variant="outlined"
        />
      ))}
      {flags.map((fl, i) => (
        <Chip
          key={`flag-${fl.code}-${i}`}
          label={labelOrCode(FLAG_CODE_LABEL, fl.code)}
          size="small"
          variant="outlined"
          color={fl.priority === "HIGH" ? "error" : "default"}
        />
      ))}
    </Stack>
  );
}

/**
 * The selectable reason codes for the "Motivo" filter — the curated failure +
 * flag label maps, each tagged with its kind so the dropdown can group them.
 * Static (not data-derived): the Examen Único code set is small and curated, so
 * a fixed list is clearer than computing facets per round.
 */
export type ReasonOption = { code: string; label: string; kind: "failure" | "flag" };

export const REASON_OPTIONS: ReasonOption[] = [
  ...Object.entries(FAILURE_CODE_LABEL).map(
    ([code, label]): ReasonOption => ({ code, label, kind: "failure" }),
  ),
  ...Object.entries(FLAG_CODE_LABEL).map(
    ([code, label]): ReasonOption => ({ code, label, kind: "flag" }),
  ),
];
